<script setup lang="ts">
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import DirectoryBreadcrumbs from '@/components/Breadcrumbs/DirectoryBreadcrumbs.vue';
import DirectorySeoLinks from '@/components/DirectorySeoLinks/DirectorySeoLinks.vue';
import { useDirectoryRouteFacets } from '@/composables/useDirectoryRouteFacets';
import { useDirectoryRouteLocations } from '@/composables/useDirectoryRouteLocations';
import { useDirectoryRouteNotFoundRedirect } from '@/composables/useDirectoryRouteNotFoundRedirect';
import { useDirectoryServerPrefetch } from '@/composables/useDirectoryServerPrefetch';
import { useDirectorySrpJsonLdContext } from '@/composables/useDirectorySrpJsonLdContext';
import { useAgentSrpJsonLdHead } from '@/composables/useDirectorySrpJsonLdHead';
import { useNewSrpIndexation } from '@/composables/useNewSrpIndexation';
import type { AgentSearchFacets } from '@/helpers/AgentDirectory/agentFacetRouteParams';
import { localizeLanguage } from '@/helpers/Locale/localizeLanguage';
import { isPlatformHg, isPlatformIs24, isServer } from '@/helpers/Platform/platform';
import { getDirectoryLocationsSeoLabel } from '@/helpers/SEO/agencyDirectorySeoText';
import {
  getAgentDirectoryPageDescription,
  getAgentDirectoryPageTitle,
  isSuccessfulEmptyAgentResults,
} from '@/helpers/SEO/agentDirectorySeoText';
import { buildDirectoryCanonicalUrl } from '@/helpers/SEO/directoryCanonicalUrl';
import AgentResultList from '@/pages/AgentDirectoryPage/AgentResultList.vue';
import HeroBanner from '@/pages/AgentDirectoryPage/HeroBanner.vue';
import { syncAgentDirectoryFromRoute } from '@/pages/AgentDirectoryPage/syncAgentDirectoryFromRoute';
import { getAgentBasePathWithLocation } from '@/router';
import { useLocale } from '@/router/routeMeta';
import { useAgentProfilesStore } from '@/stores/agentProfiles';
import { LoadState } from '@/stores/types';
import { trackAgentDirectoryPageView } from '@/tracking/tracking';

const { t } = useI18n();
const language = useLocale();
const route = useRoute();
const redirectDirectoryNotFound = useDirectoryRouteNotFoundRedirect();

const agentProfilesStore = useAgentProfilesStore();
const {
  agents,
  agentsLoadState,
  totalAgentsCount,
  agentListCurrentPage,
  pageCount,
  searchedLocations,
} = storeToRefs(agentProfilesStore);

const {
  fetchPage,
  robotsMeta,
  isIndexableForJsonLd: isNewSrpIndexableForJsonLd,
} = useNewSrpIndexation({
  locations: searchedLocations,
  pageQuery: () => route.query.page,
  totalCount: totalAgentsCount,
});

const currentPage = fetchPage;

const filterLocations = useDirectoryRouteLocations();
const filterFacets = useDirectoryRouteFacets();

const syncAgentQueryStashFromRoute = () => {
  agentProfilesStore.syncLastAgentDirectoryQueryFromRoute(route.query);
};

const loadFromRoute = async () => {
  syncAgentQueryStashFromRoute();

  await syncAgentDirectoryFromRoute({
    geoLocations: filterLocations.value,
    locale: language.value,
    currentPage: currentPage.value,
    facets: filterFacets.value,
  });
};

useDirectoryServerPrefetch({
  fetchData: loadFromRoute,
});

const buildAgentDirectoryPageViewKey = (
  page: number,
  locations: string[],
  facets: AgentSearchFacets,
): string =>
  JSON.stringify({
    page,
    locations,
    services: [...facets.services].sort(),
    specializations: [...facets.specializations].sort(),
  });

// Skip repeat pageviews when the search signature did not change (e.g. watch churn on hydrate).
let lastTrackedSearchKey: string | undefined;

watch(
  [currentPage, () => route.params.location, () => route.query.loc, filterFacets],
  async () => {
    try {
      const searchKey = buildAgentDirectoryPageViewKey(
        currentPage.value,
        filterLocations.value,
        filterFacets.value,
      );

      if (!isServer() && searchKey === lastTrackedSearchKey) {
        return;
      }

      if (!isServer()) {
        lastTrackedSearchKey = searchKey;
      }

      await loadFromRoute();

      if (isServer()) {
        return;
      }

      const currentSearchKey = buildAgentDirectoryPageViewKey(
        currentPage.value,
        filterLocations.value,
        filterFacets.value,
      );
      if (currentSearchKey !== searchKey) {
        return;
      }

      trackAgentDirectoryPageView({
        agents: agents.value,
        filterLocations: filterLocations.value,
        totalAgentsCount: totalAgentsCount.value,
        pageCount: pageCount.value,
        currentPage: currentPage.value,
        language: language.value,
      });
    } catch (error) {
      if (isServer()) {
        return;
      }

      await redirectDirectoryNotFound(error);
    }
  },
  { immediate: !isServer() },
);

onBeforeUnmount(() => {
  agentProfilesStore.markSearchedLocationsStale();
});

const hasSearchedLocations = computed(() =>
  Boolean(searchedLocations.value && searchedLocations.value.length > 0),
);

const locationNameForSeo = computed(() =>
  getDirectoryLocationsSeoLabel(searchedLocations.value, language.value),
);

const canonicalUrl = computed(() => buildDirectoryCanonicalUrl(route.path, currentPage.value));

const isSuccessfulEmpty = computed(() =>
  isSuccessfulEmptyAgentResults(agentsLoadState.value, totalAgentsCount.value),
);

const title = computed(() =>
  getAgentDirectoryPageTitle(t, {
    currentPage: currentPage.value,
    locationLabel: locationNameForSeo.value,
    count: totalAgentsCount.value,
    hasSearchedLocations: hasSearchedLocations.value,
    isSuccessfulEmpty: isSuccessfulEmpty.value,
  }),
);

const description = computed(() =>
  getAgentDirectoryPageDescription(t, {
    locationLabel: locationNameForSeo.value,
    count: totalAgentsCount.value,
    hasSearchedLocations: hasSearchedLocations.value,
    isSuccessfulEmpty: isSuccessfulEmpty.value,
  }),
);

const getLocalizedAgentDirectoryUrl = (lang: HgSupportedLanguage) =>
  `${import.meta.env.VUE_APP_BASE_URL}${getAgentBasePathWithLocation(
    lang,
    searchedLocations.value?.[0]?.urlNames?.[lang],
  )}`;

const seoLinks = computed(() => [
  {
    rel: 'canonical' as const,
    href: canonicalUrl.value,
  },
  ...Object.values(HgSupportedLanguage).map((lang) => ({
    rel: 'alternate' as const,
    hreflang: localizeLanguage(lang),
    href: getLocalizedAgentDirectoryUrl(lang),
  })),
  {
    rel: 'alternate' as const,
    hreflang: 'x-default',
    href: getLocalizedAgentDirectoryUrl(HgSupportedLanguage.DE),
  },
]);

const { locationCount, breadcrumbs } = useDirectorySrpJsonLdContext(
  'agent',
  searchedLocations,
  currentPage,
);

useHead({
  title,
  meta: () => [
    {
      name: 'description',
      content: () => description.value,
    },
    ...robotsMeta.value,
  ],
  link: seoLinks,
});

useAgentSrpJsonLdHead(() => ({
  language: language.value,
  canonicalUrl: canonicalUrl.value,
  pageTitle: title.value,
  pageDescription: description.value,
  breadcrumbs: breadcrumbs.value,
  isIndexable: isNewSrpIndexableForJsonLd.value,
  locationCount: locationCount.value,
  agents: agents.value,
  agentListCurrentPage: agentListCurrentPage.value,
  totalAgentsCount: totalAgentsCount.value,
  loadingAgents: agentsLoadState.value === LoadState.LOADING,
  agentsLoadFailed: agentsLoadState.value === LoadState.FAILED,
}));
</script>

<template>
  <div class="layout-container">
    <DirectoryBreadcrumbs
      v-if="isPlatformIs24() && searchedLocations !== null"
      directoryType="agent"
      :searchedLocations="searchedLocations ?? []"
      class="breadcrumbs"
    />
  </div>

  <HeroBanner />

  <div class="layout-container">
    <AgentResultList
      :loadingAgents="
        agentsLoadState === LoadState.UNDEFINED || agentsLoadState === LoadState.LOADING
      "
      :agentsLoadFailed="agentsLoadState === LoadState.FAILED"
      :agents="agents"
      :totalAgentsCount="totalAgentsCount"
      :agentListCurrentPage="agentListCurrentPage"
      :pageCount="pageCount"
      :language="language"
      :locationsForSeo="locationNameForSeo"
    />
    <DirectoryBreadcrumbs
      v-if="isPlatformHg() && searchedLocations !== null"
      directoryType="agent"
      :searchedLocations="searchedLocations ?? []"
      class="breadcrumbs"
    />
    <DirectorySeoLinks
      v-if="searchedLocations !== null"
      directoryType="agent"
      :searchedLocations="searchedLocations ?? []"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/variables' as *;

.breadcrumbs {
  margin-top: $space-150;
}
</style>
